import React from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {withRouter} from 'react-router';

import {AcceptanceSidebar} from 'app/components/general/DetailPage/LeftSidebar/AcceptanceSidebar';
import {ActivitySidebar} from 'app/components/general/DetailPage/LeftSidebar/ActivitySidebar';
import {LeftMainMenuButton} from 'app/components/general/DetailPage/LeftSidebar/MainLeftSidebar';
import {PermissionsSidebar} from 'app/components/general/DetailPage/LeftSidebar/PermissionsSidebar';
import {ProceduresForPolicyLeftSidebar} from 'app/components/general/DetailPage/LeftSidebar/ProceduresLeftSidebar';
import {ProcessesForPolicyLeftSidebar} from 'app/components/general/DetailPage/LeftSidebar/ProcessesLeftSidebar';
import {ReviewSidebar} from 'app/components/general/DetailPage/LeftSidebar/ReviewSidebar';
import {SignoffLogSidebar} from 'app/components/general/DetailPage/LeftSidebar/SignOffLogSidebar';
import {TeamsSidebar} from 'app/components/general/DetailPage/LeftSidebar/TeamsSidebar';
import {PolicyVersionsSidebar} from 'app/components/general/DetailPage/LeftSidebar/VersionsSidebar/VersionsSidebar';
import {DetailPageSidebarShortcuts} from 'app/general/shortcuts/DetailPageSidebarShortcuts';
import {PageSidebarStructure} from 'app/general/structures/PageSidebarStructure';
import {usePolicyByHashid, usePolicyVersionById} from 'app/policies/api';
import {useToggleWidgetIsOpen} from 'app/settings/hooks';
import {COMMENTS_WIDGET} from 'app/settings/slice';
import {getObjectActivityURL, getObjectCurrentVersionURL} from 'app/urls';
import {getPathFromURL} from 'app/utils/urls';

import type {LeftSidebarProps} from 'app/components/general/DetailPage/LeftSidebar/structure/types';
import type {WithRouterProps} from 'react-router';
import type {DetailPolicySerializedType} from 'sweet-types';

const leftSidebar = [
  PolicyVersionsSidebar,
  AcceptanceSidebar,
  ProceduresForPolicyLeftSidebar,
  ProcessesForPolicyLeftSidebar,
  TeamsSidebar,
  PermissionsSidebar,
  ActivitySidebar,
  ReviewSidebar,
  SignoffLogSidebar,
];

export const PolicyPageSidebar = withRouter<
  LeftSidebarProps<DetailPolicySerializedType>
>(function ({
  location,
  router,
  params,
  routeName,
  CurrentLeftSidebar,
}: LeftSidebarProps<DetailPolicySerializedType> & WithRouterProps) {
  const toggleWidgetIsOpen = useToggleWidgetIsOpen();

  const {versionid: versionId} = params;
  const {data: object, refetch: refetch} = usePolicyByHashid(params.hashid, {
    refetchOnMountOrArgChange: false,
  });
  const {data: version} = usePolicyVersionById(
    object == null
      ? skipToken
      : {versionId: versionId || object.current_version.id},
    {
      refetchOnMountOrArgChange: false,
    }
  );

  if (object == null) {
    return null;
  }
  const getVersionsURL = () => {
    return getPathFromURL(
      getObjectCurrentVersionURL(object, versionId, location.search)
    );
  };

  const getActivityURL = (): string => {
    return getObjectActivityURL(object, location.search);
  };
  const goToEditURL = () => {
    router.push(`${object.html_url}edit/`);
  };

  return (
    <DetailPageSidebarShortcuts
      openVersions={() => router.push(getVersionsURL())}
      openComments={() =>
        toggleWidgetIsOpen({
          contentType: 'policy',
          objectId: object.id,
          widgetName: COMMENTS_WIDGET,
        })
      }
      openActivities={() => router.push(getActivityURL())}
      edit={goToEditURL}
    >
      <PageSidebarStructure
        innerContent={
          CurrentLeftSidebar && (
            <CurrentLeftSidebar.Panel
              object={object}
              version={version}
              legacyRefetch={refetch}
            />
          )
        }
      >
        {(showTooltip, hideTooltip) => {
          return (
            <>
              <LeftMainMenuButton
                key={'left-sidebar-menu-item-main'}
                object={object}
                forceTooltipShow={showTooltip}
                hideTooltip={hideTooltip}
                location={location}
                params={params}
              />
              {leftSidebar.map((Sidebar, index) => (
                <Sidebar.MenuButton
                  key={`left-sidebar-menu-item-${index}`}
                  active={routeName === Sidebar.sidebarName || false}
                  object={object}
                  forceTooltipShow={showTooltip}
                  hideTooltip={hideTooltip}
                  location={location}
                  params={params}
                />
              ))}
            </>
          );
        }}
      </PageSidebarStructure>
    </DetailPageSidebarShortcuts>
  );
});
